export type DuplicateWorkflowInput = {
  id: string;
  name: string;
  readme: string | null;
  connectors: ReadonlyArray<{ name: string; identifier: string }>;
};

export type DuplicateWorkflowProfile = {
  id: string;
  name: string;
  purpose: string;
  tokens: Set<string>;
  connectorNames: string[];
};

export type DuplicateCandidate = {
  workflowAId: string;
  workflowBId: string;
  a: DuplicateWorkflowProfile;
  b: DuplicateWorkflowProfile;
  score: number;
};

// Recall-oriented: an LLM judge makes the precision call, so this only needs
// to keep the candidate set small.
const CANDIDATE_SIMILARITY_THRESHOLD = 0.15;

// A token shared by this many workflows (a ubiquitous connector or verb) is not
// discriminating, and pairing every workflow that carries it would make the
// inverted-index scan quadratic. Skipping those buckets keeps the scan linear;
// a genuine duplicate still shares rarer tokens, so recall barely moves.
const MAX_TOKEN_WORKFLOWS = 200;

const STOP_WORDS = new Set(
  "a an and are as at be by for from has in is it its of on or that the to was were will with this but they have had what when where who all can their there been if more not only so than too very just into also how each which then via".split(
    " "
  )
);

export function extractPurpose(readme: string | null): string {
  if (!readme) return "";
  const body: string[] = [];
  for (const line of readme.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("#")) continue;
    if (trimmed === "") {
      if (body.length > 0) break;
      continue;
    }
    body.push(trimmed);
  }
  return body.join(" ");
}

function tokenize(text: string): Set<string> {
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/);
  return new Set(words.filter((w) => w.length > 2 && !STOP_WORDS.has(w)));
}

function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  let intersection = 0;
  for (const word of a) {
    if (b.has(word)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

export function buildDuplicateProfile(
  workflow: DuplicateWorkflowInput
): DuplicateWorkflowProfile {
  const purpose = extractPurpose(workflow.readme);
  const connectorNames = [
    ...new Map(workflow.connectors.map((c) => [c.identifier, c.name])).values(),
  ];
  return {
    id: workflow.id,
    name: workflow.name,
    purpose,
    tokens: tokenize(`${workflow.name} ${purpose}`),
    connectorNames,
  };
}

export function pairKey(idA: string, idB: string): string {
  return idA < idB ? `${idA}:${idB}` : `${idB}:${idA}`;
}

// A similarity above the threshold requires a shared token, so the
// inverted-index prefilter keeps the scan near-linear.
export function computeDuplicateCandidates(
  workflows: ReadonlyArray<DuplicateWorkflowInput>
): DuplicateCandidate[] {
  const profiles = workflows.map(buildDuplicateProfile);
  const byToken = new Map<string, number[]>();
  profiles.forEach((profile, i) => {
    for (const token of profile.tokens) {
      const bucket = byToken.get(token);
      if (bucket) bucket.push(i);
      else byToken.set(token, [i]);
    }
  });

  const seen = new Set<number>();
  for (const bucket of byToken.values()) {
    if (bucket.length > MAX_TOKEN_WORKFLOWS) continue;
    for (let x = 0; x < bucket.length; x++) {
      for (let y = x + 1; y < bucket.length; y++) {
        seen.add(bucket[x]! * profiles.length + bucket[y]!);
      }
    }
  }

  const candidates: DuplicateCandidate[] = [];
  for (const encoded of seen) {
    const a = profiles[Math.floor(encoded / profiles.length)]!;
    const b = profiles[encoded % profiles.length]!;
    const score = jaccardSimilarity(a.tokens, b.tokens);
    if (score < CANDIDATE_SIMILARITY_THRESHOLD) continue;
    const sorted = a.id < b.id;
    candidates.push({
      workflowAId: sorted ? a.id : b.id,
      workflowBId: sorted ? b.id : a.id,
      a: sorted ? a : b,
      b: sorted ? b : a,
      score,
    });
  }

  return candidates.sort(
    (x, y) =>
      y.score - x.score ||
      `${x.workflowAId}:${x.workflowBId}`.localeCompare(
        `${y.workflowAId}:${y.workflowBId}`
      )
  );
}

// Pairs confirmed “same” merge into one group (union-find) and covered pairs
// are skipped, so a cluster of k look-alikes asks k−1 questions; a “different”
// verdict splits the group and surfaces follow-ups on the next computation.
export function pendingDuplicatePairs<
  T extends { workflowAId: string; workflowBId: string },
>(
  pairs: ReadonlyArray<T>,
  reviews: ReadonlyArray<{
    workflowAId: string;
    workflowBId: string;
    decision: string;
  }>
): T[] {
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== undefined) root = parent.get(root)!;
    return root;
  };

  const reviewed = new Set<string>();
  for (const review of reviews) {
    reviewed.add(pairKey(review.workflowAId, review.workflowBId));
    if (review.decision === "same") {
      const [rootA, rootB] = [
        find(review.workflowAId),
        find(review.workflowBId),
      ];
      if (rootA !== rootB) parent.set(rootA, rootB);
    }
  }

  const pending: T[] = [];
  for (const pair of pairs) {
    if (reviewed.has(pairKey(pair.workflowAId, pair.workflowBId))) continue;
    const [rootA, rootB] = [find(pair.workflowAId), find(pair.workflowBId)];
    if (rootA === rootB) continue;
    parent.set(rootA, rootB);
    pending.push(pair);
  }
  return pending;
}
